import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Analytics } from '@vercel/analytics/react';
import { trackPageView } from './utils/analytics';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import PromptPlaza from './components/PromptPlaza';
import AgentMarket from './components/AgentMarket';
import MyAgents from './components/MyAgents';
import PaymentResult from './components/PaymentResult';

import Navbar from './components/Navbar';
import ScrollIndicator from './components/ScrollIndicator';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AboutUs from './components/AboutUs';
import UserProfile from './components/UserProfile';
import VersionLog from './components/VersionLog';
import SecretKeyManager from './components/SecretKeyManager';
import MediaManager from './components/MediaManager';
import BillingCenter from './components/BillingCenter';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PromptProvider } from './contexts/PromptContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PerformanceMonitor } from './utils/performance';

// 加载中组件
const LoadingSpinner: React.FC = () => (
  <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-300 font-medium">Loading...</p>
    </div>
  </div>
);

// 受保护路由组件
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
};

// 路由追踪组件
const RouteTracker: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  
  useEffect(() => {
    // 将路径转换为页面名称用于追踪
    const pathToPage: Record<string, string> = {
      '/': 'plaza',
      '/plaza': 'plaza',
      '/dashboard': 'dashboard',
      '/login': 'login',
      '/about': 'about',
      '/privacy': 'privacy',
      '/terms': 'terms',
      '/agent-market': 'agent-market',
      '/my-agents': 'my-agents',
      '/mcp': 'mcp',
    };
    const pageName = pathToPage[location.pathname] || location.pathname;
    trackPageView(pageName, isAuthenticated);
  }, [location.pathname, isAuthenticated]);
  
  return null;
};

function AppContent() {
  const { isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Modal 状态
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showVersionLog, setShowVersionLog] = useState(false);
  const [showSecretKeyManager, setShowSecretKeyManager] = useState(false);
  const [showMediaManager, setShowMediaManager] = useState(false);
  const [showBillingCenter, setShowBillingCenter] = useState(false);

  // 初始化Core Web Vitals监控
  useEffect(() => {
    PerformanceMonitor.initCoreWebVitals();
    
    // 开发环境下每10秒打印一次性能指标
    if (process.env.NODE_ENV === 'development') {
      const interval = setInterval(() => {
        const vitals = PerformanceMonitor.getCoreWebVitals();
        console.log('📊 Core Web Vitals:', vitals);
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, []);

  // 处理函数
  const handleLogout = () => {
    logout();
    navigate('/plaza');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLoginSuccess = () => {
    // 如果有之前尝试访问的页面，返回那里
    const from = (location.state as any)?.from?.pathname || '/dashboard';
    navigate(from, { replace: true });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 font-inter overflow-x-hidden flex flex-col">
      <RouteTracker />
      <ScrollIndicator />
      <Navbar 
        onLogout={handleLogout}
        onLogin={handleLogin}
        isAuthenticated={isAuthenticated}
        onProfileEdit={() => setShowUserProfile(true)}
        onVersionLog={() => setShowVersionLog(true)}
        onSecretKeyManage={() => setShowSecretKeyManager(true)}
        onMediaManage={() => setShowMediaManager(true)}
        onBillingCenter={() => setShowBillingCenter(true)}
      />
      <div className="pt-20 flex-1">
        <Routes>
          {/* 首页重定向到广场 */}
          <Route path="/" element={<Navigate to="/plaza" replace />} />
          
          {/* 公开路由 */}
          <Route path="/plaza" element={
            <PromptProvider>
              <PromptPlaza onPromptCopied={() => {}} />
            </PromptProvider>
          } />
          <Route path="/agent-market" element={
            <AgentMarket isAuthenticated={isAuthenticated} />
          } />
          <Route path="/login" element={
            isAuthenticated 
              ? <Navigate to="/dashboard" replace /> 
              : <LoginPage onLoginSuccess={handleLoginSuccess} onShowLogin={handleLogin} />
          } />
          <Route path="/about" element={<AboutUs onClose={() => navigate('/plaza')} />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/payment/result" element={<PaymentResult />} />
          
          {/* 受保护路由 */}
          <Route path="/dashboard" element={
            <PrivateRoute>
              <PromptProvider>
                <Dashboard />
              </PromptProvider>
            </PrivateRoute>
          } />
          <Route path="/my-agents" element={
            <PrivateRoute>
              <MyAgents isAuthenticated={true} onLoginRequired={handleLogin} />
            </PrivateRoute>
          } />
          
          {/* 404 - 重定向到广场 */}
          <Route path="*" element={<Navigate to="/plaza" replace />} />
        </Routes>
      </div>
      <Footer />
      
      {/* 用户资料编辑模态框 */}
      {isAuthenticated && (
        <UserProfile 
          isOpen={showUserProfile}
          onClose={() => setShowUserProfile(false)}
        />
      )}
      
      {/* 版本日志模态框 */}
      <VersionLog 
        isOpen={showVersionLog}
        onClose={() => setShowVersionLog(false)}
      />
      
      {/* Secret Key 管理 */}
      {isAuthenticated && (
        <SecretKeyManager 
          isOpen={showSecretKeyManager}
          onClose={() => setShowSecretKeyManager(false)}
        />
      )}

      {/* 媒体管理（管理员专用） */}
      {isAuthenticated && (
        <MediaManager
          isOpen={showMediaManager}
          onClose={() => setShowMediaManager(false)}
        />
      )}

      {/* 账单中心 */}
      {isAuthenticated && (
        <BillingCenter
          isOpen={showBillingCenter}
          onClose={() => setShowBillingCenter(false)}
        />
      )}
      
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          className: 'dark:bg-gray-800 dark:text-white',
          style: {
            background: 'var(--toast-bg, #fff)',
            color: 'var(--toast-color, #374151)',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
          <Analytics />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
